import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import EigooMark from "../components/EigooMark";
import LifecycleTimeline, { PriorityBadge } from "../components/LifecycleTimeline";
import { toast } from "sonner";
import {
    Send,
    ClipboardCheck,
    Search,
    Sparkles,
    ArrowLeft,
    CheckCircle2,
    Clock,
    Hourglass,
    Briefcase,
} from "lucide-react";

const currency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_META = {
    pending: { label: "Awaiting operative", color: "hsl(45 85% 68%)", icon: Hourglass },
    accepted: { label: "Operative assigned", color: "hsl(40 75% 60%)", icon: ClipboardCheck },
    in_progress: { label: "Delivery in progress", color: "hsl(45 85% 60%)", icon: Briefcase },
    on_hold: { label: "On hold", color: "hsl(25 70% 60%)", icon: Clock },
    completed: { label: "Completed", color: "hsl(153 55% 60%)", icon: CheckCircle2 },
    cancelled: { label: "Cancelled", color: "hsl(350 75% 65%)", icon: Clock },
};

function ServiceTile({ s, selected, onSelect }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(s.code)}
            data-testid={`service-${s.code}`}
            className={`text-left p-5 rounded-2xl border transition-all ${
                selected
                    ? "border-[hsl(45_85%_68%)] bg-[hsl(40_60%_18%/0.4)] shadow-[0_8px_30px_-12px_hsl(40_70%_45%/0.4)]"
                    : "border-[hsl(40_40%_25%/0.3)] hover:border-[hsl(45_85%_68%/0.5)]"
            }`}
        >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">{s.category}</div>
            <div className="font-heading text-lg mt-1 tracking-tight">{s.name}</div>
            <div className="text-xs text-muted-foreground mt-2 leading-relaxed line-clamp-3">{s.description}</div>
            <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{s.duration}</span>
                <span className="font-heading text-xl gold-gradient-text">{currency(s.price)}</span>
            </div>
        </button>
    );
}

function TrackingPanel({ initial }) {
    const [code, setCode] = useState(initial || "");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const lookup = async (e) => {
        if (e) e.preventDefault();
        if (!code.trim()) return;
        setBusy(true);
        setErr("");
        setData(null);
        try {
            const res = await api.get(`/foundry/orders/track/${code.trim()}`);
            setData(res.data);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || "Tracking failed");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initial) lookup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial]);

    const Icon = data ? STATUS_META[data.status]?.icon || Clock : null;

    return (
        <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]" data-testid="track-panel">
            <div className="flex items-center gap-2 mb-3">
                <Search size={16} className="text-[hsl(45_85%_68%)]" />
                <h3 className="font-heading text-xl">Track a Mission</h3>
            </div>
            <form onSubmit={lookup} className="flex gap-2">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Tracking code"
                    data-testid="track-input"
                    className="flex-1 px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] font-mono text-sm"
                />
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="track-submit"
                    className="px-5 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                >
                    {busy ? "..." : "Track"}
                </button>
            </form>

            {err && <div className="mt-4 text-sm text-[hsl(350_80%_72%)]">{err}</div>}

            {data && (
                <div className="mt-5 space-y-4 fade-up" data-testid="track-result">
                    <div className="flex items-start gap-3">
                        {Icon && <Icon size={24} style={{ color: STATUS_META[data.status]?.color }} />}
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: STATUS_META[data.status]?.color }}>
                                    {STATUS_META[data.status]?.label || data.status}
                                </div>
                                {data.priority && data.priority !== "standard" && (
                                    <PriorityBadge priority={data.priority} />
                                )}
                            </div>
                            <div className="font-heading text-2xl tracking-tight mt-1">{data.service_name}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {data.is_custom ? "Custom Sovereign Delivery" : data.category} · {currency(data.amount)}
                            </div>
                        </div>
                    </div>

                    {/* 5-stage lifecycle timeline (Director directive 2026.02) */}
                    <div className="pt-4 mt-2 border-t border-[hsl(40_40%_30%/0.18)]">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
                            Delivery Lifecycle
                        </div>
                        <LifecycleTimeline stage={data.stage || data.status} />
                    </div>

                    {data.accepted_by_name && (
                        <div className="text-xs text-muted-foreground">
                            Operative: <span className="text-foreground/90">{data.accepted_by_name}</span>
                        </div>
                    )}
                    {data.history?.length > 0 && (
                        <ol className="border-l border-[hsl(40_40%_30%/0.3)] pl-4 space-y-2 text-xs">
                            {data.history.map((h, i) => (
                                <li key={i} className="relative">
                                    <span className="absolute -left-[19px] top-1 w-2 h-2 rounded-full bg-[hsl(45_85%_68%)]" />
                                    <div className="text-foreground/85 capitalize">{h.event.replace("_", " ")}</div>
                                    <div className="text-muted-foreground text-[10px]">{new Date(h.at).toLocaleString()}</div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}
        </div>
    );
}

export default function MissionControl() {
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("all");
    const [mode, setMode] = useState("catalog"); // catalog | custom
    const [serviceCode, setServiceCode] = useState(null);
    const [form, setForm] = useState({ client_name: "", client_email: "", custom_brief: "", target_amount: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        api.get("/foundry/services").then((r) => {
            setServices(r.data.services);
            setCategories(["all", ...r.data.categories]);
        });
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                client_name: form.client_name,
                client_email: form.client_email,
                notes: form.notes || null,
            };
            if (mode === "catalog") {
                if (!serviceCode) { toast.error("Pick a service first."); setBusy(false); return; }
                payload.service_code = serviceCode;
            } else {
                if (!form.custom_brief || !form.target_amount) {
                    toast.error("Custom deliveries need a brief and target amount.");
                    setBusy(false);
                    return;
                }
                payload.custom_brief = form.custom_brief;
                payload.target_amount = parseFloat(form.target_amount);
            }
            const { data } = await api.post("/foundry/orders", payload);
            setSuccess(data);
            toast.success("Mission filed. Save your tracking code.");
        } catch (ex) {
            toast.error(formatApiError(ex.response?.data?.detail) || "Submission failed");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setSuccess(null);
        setServiceCode(null);
        setForm({ client_name: "", client_email: "", custom_brief: "", target_amount: "", notes: "" });
    };

    const visible = activeCat === "all" ? services : services.filter((s) => s.category === activeCat);

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid="mission-control">
            <div className="absolute inset-0 pointer-events-none opacity-30">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.10)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.20)] blur-[120px]" />
            </div>

            <header className="relative max-w-6xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
                <EigooMark size="lg" sub="Mission Control" testid="mc-mark" />
                <Link to="/" className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back to paths
                </Link>
            </header>

            <main className="relative max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] mb-3 flex items-center gap-2">
                            <Sparkles size={11} /> File a Mission
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            What do you need
                            <span className="block gold-gradient-text mt-2 italic font-normal">delivered today?</span>
                        </h1>
                        <p
                            className="mt-5 text-lg text-foreground/80 max-w-2xl leading-relaxed"
                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                        >
                            Pick a standardised service from the EIGOO catalog, or commission a Custom
                            Sovereign Delivery. We dispatch a vetted Field Operative —
                            a route charted by the right badges, the right Foundry, the right covenant.
                        </p>
                    </div>

                    {success ? (
                        <div className="glass rounded-2xl p-7 border-2 border-[hsl(45_85%_68%/0.6)]" data-testid="mc-success">
                            <div className="flex items-center gap-2 text-[hsl(45_85%_68%)] uppercase tracking-[0.3em] text-[10px]">
                                <CheckCircle2 size={14} /> Mission filed
                            </div>
                            <div className="font-heading text-3xl mt-2 tracking-tight">{success.service_name}</div>
                            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                                <div className="rounded-md border border-[hsl(40_40%_25%/0.3)] p-3">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Tracking code</div>
                                    <div className="font-mono text-base mt-1 text-[hsl(45_85%_68%)]" data-testid="mc-tracking-code">{success.tracking_code}</div>
                                </div>
                                <div className="rounded-md border border-[hsl(40_40%_25%/0.3)] p-3">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Total</div>
                                    <div className="font-heading text-2xl mt-1 gold-gradient-text">{currency(success.amount)}</div>
                                </div>
                            </div>
                            <p className="mt-4 text-sm text-muted-foreground">
                                Save your tracking code. The delivery will surface in the Field Operative feed —
                                a vetted operator typically picks up the signal within 24 hours.
                            </p>
                            <div className="mt-6 flex gap-3 flex-wrap">
                                <button
                                    onClick={reset}
                                    data-testid="mc-file-another"
                                    className="px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                                >
                                    File another mission
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 border-b border-[hsl(40_40%_25%/0.25)] pb-3">
                                <button
                                    onClick={() => setMode("catalog")}
                                    data-testid="mc-mode-catalog"
                                    className={`px-4 py-2 text-sm uppercase tracking-[0.2em] border-b-2 -mb-[14px] transition-colors ${
                                        mode === "catalog"
                                            ? "border-[hsl(45_85%_68%)] text-[hsl(45_85%_68%)]"
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    Catalog
                                </button>
                                <button
                                    onClick={() => setMode("custom")}
                                    data-testid="mc-mode-custom"
                                    className={`px-4 py-2 text-sm uppercase tracking-[0.2em] border-b-2 -mb-[14px] transition-colors ${
                                        mode === "custom"
                                            ? "border-[hsl(45_85%_68%)] text-[hsl(45_85%_68%)]"
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    Custom Sovereign Delivery
                                </button>
                            </div>

                            {mode === "catalog" && (
                                <div data-testid="mc-catalog">
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {categories.map((c) => (
                                            <button
                                                key={c}
                                                onClick={() => setActiveCat(c)}
                                                data-testid={`mc-cat-${c}`}
                                                className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                                                    activeCat === c
                                                        ? "border-[hsl(45_85%_68%)] text-[hsl(45_85%_68%)] bg-[hsl(40_60%_18%/0.4)]"
                                                        : "border-[hsl(40_40%_25%/0.3)] text-muted-foreground hover:border-[hsl(45_85%_68%/0.5)]"
                                                }`}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {visible.map((s) => (
                                            <ServiceTile key={s.code} s={s} selected={serviceCode === s.code} onSelect={setServiceCode} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            <form onSubmit={submit} className="space-y-4 glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.3)]" data-testid="mc-form">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Your name</label>
                                        <input
                                            required
                                            value={form.client_name}
                                            onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                                            data-testid="mc-name"
                                            className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                                        <input
                                            type="email"
                                            required
                                            value={form.client_email}
                                            onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                                            data-testid="mc-email"
                                            className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                        />
                                    </div>
                                </div>

                                {mode === "custom" && (
                                    <>
                                        <div>
                                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Delivery brief</label>
                                            <textarea
                                                required
                                                rows={5}
                                                value={form.custom_brief}
                                                onChange={(e) => setForm({ ...form, custom_brief: e.target.value })}
                                                data-testid="mc-brief"
                                                placeholder="Describe the work. Be specific about constraints, timelines, and outcomes."
                                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] resize-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Target budget (USD)</label>
                                            <input
                                                required
                                                type="number"
                                                min="100"
                                                step="100"
                                                value={form.target_amount}
                                                onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                                                data-testid="mc-budget"
                                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                                            />
                                        </div>
                                    </>
                                )}

                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Notes (optional)</label>
                                    <textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        data-testid="mc-notes"
                                        className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="mc-submit"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                                >
                                    <Send size={16} />
                                    {busy ? "Filing delivery..." : "File Mission"}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                <aside>
                    <TrackingPanel />
                    <div className="mt-6 glass rounded-2xl p-5 border border-[hsl(40_40%_25%/0.25)] text-xs text-muted-foreground leading-relaxed">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] mb-2">Sovereign 60/30/10</div>
                        <p>
                            Every completed delivery pays out 60% to the operative,
                            30% to the EIGOO Fleet, 10% to CyberKids.
                            Splits accrue automatically in the Foundry Ledger.
                        </p>
                    </div>
                </aside>
            </main>
        </div>
    );
}
